/**
 * forecastApi.js — read-only data layer for the live Forecast Accuracy page.
 *
 * The data is published as static JSON to the CDN by an EKS job (decoupled from
 * the app cluster — the landing stays always-up). Same shape the page + PDF
 * exporters already consume, so swapping the static import for these fetchers is
 * the only change needed downstream.
 *
 *   index.json              manifest (date range, months, rolling/all-time)
 *   months/<YYYY-MM>.json    per-month daily summaries (pills + table)
 *   days/<YYYY-MM-DD>.json   one day's 96 blocks + summary (chart + PDF)
 *   forecast/latest.json     the live T+1 DAM forecast (no actual yet)
 *
 * Base URL defaults to the same-origin CDN path; override with
 * REACT_APP_FORECAST_DATA_URL for local dev pointing at a copy under public/.
 */
import useSWR from "swr";

export const FORECAST_DATA_BASE = (
  process.env.REACT_APP_FORECAST_DATA_URL || "/data/forecast-accuracy"
).replace(/\/+$/, "");

export const indexUrl = () => `${FORECAST_DATA_BASE}/index.json`;
export const monthUrl = (ym) => `${FORECAST_DATA_BASE}/months/${ym}.json`;
export const dayUrl = (d) => `${FORECAST_DATA_BASE}/days/${d}.json`;
export const latestUrl = () => `${FORECAST_DATA_BASE}/forecast/latest.json`;

/**
 * fetch + parse JSON. Guards against the SPA 403/404→index.html fallback that
 * CloudFront serves for a missing object: a non-JSON 200 is treated as missing
 * (status 404) so the UI shows an empty state, not a crash.
 */
export async function getJson(url) {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status} for ${url}`);
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("json")) {
    const err = new Error(`non-JSON response for ${url} (likely missing object)`);
    err.status = 404;
    throw err;
  }
  return res.json();
}

const SWR_OPTS = { revalidateOnFocus: false, shouldRetryOnError: false };

export function useForecastIndex() {
  return useSWR(indexUrl(), getJson, SWR_OPTS);
}

export function useForecastMonth(ym) {
  return useSWR(ym ? monthUrl(ym) : null, getJson, SWR_OPTS);
}

export function useForecastDay(dateStr) {
  return useSWR(dateStr ? dayUrl(dateStr) : null, getJson, SWR_OPTS);
}

/**
 * Merge every month manifest into one date-sorted list of day summaries.
 * Powers the week-based picker (slice into 7-day windows over settled days only).
 */
export function useAllSettledDays(months) {
  const key = months?.length ? `fa:all-days:${months.join(",")}` : null;
  return useSWR(
    key,
    async () => {
      const docs = await Promise.all(
        months.map((m) => getJson(monthUrl(m)).catch(() => null)),
      );
      const days = docs.filter(Boolean).flatMap((d) => d.days || []);
      days.sort((a, b) => (a.date < b.date ? -1 : 1));
      return days;
    },
    SWR_OPTS,
  );
}

/** Live T+1 forecast — refresh every 5 min so the "Tomorrow" pill stays fresh. */
export function useLatestForecast() {
  return useSWR(latestUrl(), getJson, { ...SWR_OPTS, refreshInterval: 5 * 60 * 1000 });
}

/** Fetch several day docs (for PDF export over a range). Missing days are skipped. */
export async function fetchDays(dateStrs) {
  const docs = await Promise.all(
    dateStrs.map((d) => getJson(dayUrl(d)).catch(() => null)),
  );
  return docs.filter(Boolean);
}
